/**
 * A flat, one-row-per-manager-per-game view of `LeagueHistory` — the same
 * shape as the old scramble `team_games` SQL view. Every finished game appears
 * twice, once from each side, so any "how has X done" question is a filter.
 */

import type { GameType, LeagueHistory } from "../history";

export type GameResult = "W" | "L" | "T";

export interface PersonGame {
  season: number;
  week: number;
  gameType: GameType;
  personId: string;
  opponentId: string;
  teamName: string;
  opponentTeamName: string;
  points: number;
  opponentPoints: number;
  projected: number | null;
  opponentProjected: number | null;
  result: GameResult;
}

/** Oldest first (season, then week). */
export function buildLedger(history: LeagueHistory): PersonGame[] {
  const rows: PersonGame[] = [];
  for (const season of history.seasons) {
    const teamName = new Map(season.teams.map((t) => [t.teamKey, t.name]));
    for (const game of season.games) {
      const [a, b] = game.sides;
      for (const [mine, theirs] of [
        [a, b],
        [b, a],
      ] as const) {
        const personId = history.personByTeamKey[mine.teamKey];
        const opponentId = history.personByTeamKey[theirs.teamKey];
        if (!personId || !opponentId) continue;
        rows.push({
          season: season.season,
          week: game.week,
          gameType: game.gameType,
          personId,
          opponentId,
          teamName: teamName.get(mine.teamKey) ?? mine.teamKey,
          opponentTeamName: teamName.get(theirs.teamKey) ?? theirs.teamKey,
          points: mine.points,
          opponentPoints: theirs.points,
          projected: mine.projectedPoints,
          opponentProjected: theirs.projectedPoints,
          result: game.isTied ? "T" : mine.points > theirs.points ? "W" : "L",
        });
      }
    }
  }
  return rows.sort((x, y) => x.season - y.season || x.week - y.week);
}

/** Head-to-head and "all-time" stats leave consolation games out. */
export function countsTowardHistory(game: PersonGame): boolean {
  return game.gameType !== "consolation";
}

export interface Record3 {
  wins: number;
  losses: number;
  ties: number;
}

export function recordOf(games: readonly PersonGame[]): Record3 {
  return {
    wins: games.filter((g) => g.result === "W").length,
    losses: games.filter((g) => g.result === "L").length,
    ties: games.filter((g) => g.result === "T").length,
  };
}

/**
 * Meetings before a head-to-head record says something about the pair rather
 * than a coin flip: a profile's nemesis or favorite victim, and the report's
 * "the only one X has never beaten".
 */
export const MIN_MEETINGS_FOR_RIVAL = 4;

/** One manager's record against one opponent. */
export interface OpponentRecord extends Record3 {
  opponentId: string;
  games: number;
}

/**
 * A manager's record against each opponent, in the order first met, from
 * that manager's own games (filter them first, e.g. with
 * `countsTowardHistory`). The one head-to-head tally behind both the
 * profiles' rivals and the report's "only one" facts, so they can't disagree.
 */
export function recordsByOpponent(games: readonly PersonGame[]): OpponentRecord[] {
  const byOpponent = new Map<string, PersonGame[]>();
  for (const g of games) byOpponent.set(g.opponentId, [...(byOpponent.get(g.opponentId) ?? []), g]);
  return [...byOpponent].map(([opponentId, list]) => ({ opponentId, games: list.length, ...recordOf(list) }));
}
